import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDate } from "../../../Components/Common/FormatDate";
import { Link, useNavigate } from "react-router-dom";
import FranchiseEnquiryService from "../../../services/KangarooServices/FranchiseEnquiryService";
import UpdateStatus from "../component/UpdateStatus";
import FranchisePrivateDetail from "../component/FranchisePrivateDetail";
import { Eye } from "lucide-react";

const FranchisePrivate = () => {
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isPrivateDetailOpen, setIsPrivateDetailOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalRows, setTotalRows] = useState(0);

  const [filters, setFilters] = useState({ search: "" });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const togglePrivateDetail = (item) => {
    setSelectedItem(item);
    setIsPrivateDetailOpen(!isPrivateDetailOpen);
  };

  const fetchData = async (
    page = 1,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) => {
    try {
      setIsLoading(true);

      const res = await FranchiseEnquiryService.getPrivateList({
        ...currentFilters,
        page,
        pageSize: size,
      });

      setCourseData(res.data || []);
      setTotalRows(res.totalCount || 0);
    } catch (err) {
      console.error("ERROR:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1); // reset page on search
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);
  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedFilters);
  }, [currentPage, pageSize, debouncedFilters]);

  const totalPages = Math.ceil(totalRows / pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };
  const handleFilterChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Franchise Private Enquiry"
          pageTitle="User Management"
        />
        <Row>
          <Col lg={6}>
            <Input
              type="text"
              placeholder="Search by all"
              value={filters.search}
              onChange={handleFilterChange}
            />
          </Col>
        </Row>
        {isLoading ? (
          <div className="text-center my-4">Loading...</div>
        ) : (
          <>
            <Row>
              {courseData?.map((item, index) => {
                return (
                  <Col className="mt-4" sm={6} xl={4} key={item.id || index}>
                    <Card className="mb-3 border-0 shadow-sm h-100 d-flex flex-column hover-lift transition">
                      <CardHeader>
                        <Row>
                          <Col md={2}>
                            <div className="avatar-sm">
                              <div className="avatar-title rounded bg-success-subtle text-success fs-14 fw-semibold">
                                {item?.fullName
                                  ?.split(" ")
                                  .slice(0, 2) // take first and last name
                                  .map((name) => name.charAt(0).toUpperCase())
                                  .join("")}
                              </div>
                            </div>
                          </Col>

                          <Col md={8}>
                            <div className="d-flex justify-content-start align-content-center align-items-center ps-2 h-100">
                              <h5 className=" fw-semibold">{item?.fullName}</h5>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => togglePrivateDetail(item)}
                              >
                                <Eye size={16} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </CardHeader>

                      <CardBody className="flex-grow-1 d-flex flex-column">
                        <div className="">
                          <div className="mb-2 d-flex align-items-center">
                            <i className="bx bx-envelope text-primary me-2"></i>
                            <span className="small">{item?.email}</span>
                          </div>

                          <div className="mb-2 d-flex align-items-center">
                            <i className="bx bx-phone text-primary me-2"></i>
                            <span className="small">
                              {item?.phoneCountryCode} {item?.phone}
                            </span>
                          </div>

                          <div className=" d-flex align-items-center">
                            <i className="bx bx-time-five text-success me-2"></i>
                            <span className="small">
                              {formatDate(item?.createdAt)}
                            </span>
                          </div>
                        </div>
                        <hr className="text-muted" />

                        <div className="d-flex justify-content-between align-content-center align-items-center ">
                          <p className="d-flex align-items-center">
                            {item?.message?.length > 100
                              ? item.message.slice(0, 100) + "..."
                              : item?.message}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {totalPages > 0 && (
              <div className="d-flex justify-content-center my-4">
                <Pagination>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>

                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    const page = currentPage <= 4 ? i + 1 : i + currentPage - 4;
                    if (page > totalPages) return null;

                    return (
                      <PaginationItem key={page} active={currentPage === page}>
                        <PaginationLink onClick={() => setCurrentPage(page)}>
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
        <FranchisePrivateDetail
          isOpen={isPrivateDetailOpen}
          toggle={togglePrivateDetail}
          selectedItem={selectedItem}
        />
      </Container>
    </div>
  );
};

export default FranchisePrivate;
